import React, { useState, useEffect, useRef } from 'react';
import { ConstructorEventUpdateSQLStatus } from "../../../Event/event";
import Header from "../../Header";

const FastStackChallenge = () => {
  const [a, setA] = useState(0);
  const [b, setB] = useState(0);
  const [total, setTotal] = useState(0);
  const [steps, setSteps] = useState([]);
  const [timeLeft, setTimeLeft] = useState(15);
  const [inputValue, setInputValue] = useState('');
  const [feedback, setFeedback] = useState('');
  const [badge, setBadge] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const timerRef = useRef(null);

  // 🎨 Styles with gradients and animations
  const styles = {
    body: {
      fontFamily: `'Poppins', sans-serif`,
      textAlign: 'center',
      padding: '2rem 1rem',
      minHeight: '100vh',
      boxSizing: 'border-box',
      background: 'linear-gradient(270deg, #ff9a9e, #fad0c4, #fbc2eb, #a6c1ee)',
      backgroundSize: '600% 600%',
      animation: 'gradientBG 12s ease infinite',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    h1: {
      fontSize: '2.8rem',
      fontWeight: 'bold',
      textShadow: '2px 2px 12px rgba(0,0,0,0.3)',
      marginBottom: '1rem',
    },
    challenge: {
      fontSize: '2.4rem',
      margin: '1rem 0',
      fontWeight: 700,
      background: 'rgba(255,255,255,0.2)',
      padding: '1rem 2rem',
      borderRadius: '1rem',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    },
    step: {
      fontSize: '1.2rem',
      margin: '0.5rem 0',
      padding: '0.5rem 1rem',
      background: 'rgba(0,0,0,0.2)',
      borderRadius: '10px',
      display: 'inline-block',
    },
    timer: {
      fontSize: '1.6rem',
      fontWeight: 'bold',
      margin: '1rem 0',
      padding: '0.5rem 1rem',
      borderRadius: '12px',
      background: timeLeft <= 5 ? '#ff5252' : '#43a047',
      color: '#fff',
      boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
      transition: 'all 0.3s ease',
    },
    input: {
      fontSize: '1.3rem',
      padding: '0.7rem 1rem',
      borderRadius: '0.8rem',
      border: '3px solid #fff',
      textAlign: 'center',
      width: '100%',
      maxWidth: '160px',
      display: 'block',
      margin: '1.5rem auto',
      outline: 'none',
      background: 'rgba(255,255,255,0.2)',
      color: '#fff',
    },
    button: {
      fontSize: '1.2rem',
      padding: '0.9rem 2rem',
      background: 'linear-gradient(135deg, #ff6f61, #f57c00)',
      border: 'none',
      borderRadius: '1.2rem',
      color: 'white',
      fontWeight: 700,
      cursor: 'pointer',
      margin: '0.8rem auto',
      display: 'block',
      width: 'fit-content',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
    },
    buttonHover: {
      transform: 'scale(1.05)',
      boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
    },
    feedback: {
      fontSize: '1.4rem',
      fontWeight: 700,
      marginTop: '1rem',
      minHeight: '32px',
      textShadow: '1px 1px 6px rgba(0,0,0,0.3)',
    },
    badge: {
      fontSize: '3rem',
      marginTop: '0.8rem',
      animation: badge ? 'pop 0.6s ease forwards' : 'none',
    },
    keyframes: `
      @keyframes gradientBG {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes pop {
        0% { transform: scale(0.5); opacity: 0; }
        70% { transform: scale(1.2); opacity: 1; }
        100% { transform: scale(1); }
      }
    `
  };

  const startChallenge = () => {
    setFeedback('');
    setBadge('');
    setDisabled(false);
    setShowNext(false);
    setInputValue('');

    const num1 = Math.floor(Math.random() * 90 + 10);
    const num2 = Math.floor(Math.random() * 90 + 10);
    const sum = num1 + num2;
    setA(num1);
    setB(num2);
    setTotal(sum);
    setTimeLeft(40);

    const tensA = Math.floor(num1 / 10) * 10;
    const onesA = num1 % 10;
    const tensB = Math.floor(num2 / 10) * 10;
    const onesB = num2 % 10;

    setSteps([
      `👣 Step 1: ${tensA} + ${tensB} = ${tensA + tensB}`,
      `👣 Step 2: ${onesA} + ${onesB} = ${onesA + onesB}`,
      `👣 Final: ${tensA + tensB} + ${onesA + onesB} = ?`,
    ]);

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setFeedback('⏰ Time’s up! Try again.');
          setDisabled(true);
          setShowNext(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = () => {
    const answer = parseInt(inputValue.trim(), 10);
    if (answer === total) {
      clearInterval(timerRef.current);
      setFeedback('🎉 Correct! You earned a badge!');
      setBadge('🏅');
      const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
      setDisabled(true);
      setShowNext(true);
    } else {
      setFeedback('❌ Not quite. Try again!');
    }
  };

  useEffect(() => {
    startChallenge();
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div style={styles.body}>
      {/* <Header /> */}
      <style>{styles.keyframes}</style>

      <h1 style={styles.h1}>⚡ Fast Stack Challenge</h1>
      <div style={styles.challenge}>{`${a} + ${b}`}</div>

      <div>
        {steps.map((step, idx) => (
          <div key={idx} style={styles.step}>{step}</div>
        ))}
      </div>

      <div style={styles.timer}>⏳ {timeLeft}s</div>

      <input
        type="number"
        placeholder="Your Answer"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        style={styles.input}
        disabled={disabled}
      />

      <button 
        onClick={handleSubmit} 
        style={styles.button} 
        disabled={disabled}
        onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
      >
        Submit
      </button>

      {showNext && (
        <button
          onClick={startChallenge}
          style={{ ...styles.button, background: 'linear-gradient(135deg, #43a047, #66bb6a)' }}
        >
          Next Question
        </button>
      )}

      <div style={styles.feedback}>{feedback}</div>
      <div style={styles.badge}>{badge}</div>
    </div>
  );
};

export default FastStackChallenge;
